import {APP_INITIALIZER, Provider} from '@angular/core';
import {BdDynamicUiComponentLoaderService} from './bd-dynamic-ui-component-loader.service';
import {BdDunningExitUiComponent} from '../enum/bd-dunning-exit-ui-component.enum';
import {
    BdContractDecisionTableComponent
} from '../component/uicomponent/bd-contract-decision-table/bd-contract-decision-table.component';
import {
    BdLvDecisionTableComponent
} from '../component/uicomponent/bd-lv-decision-table/bd-lv-decision-table.component';
import {
    BdHandoverApprovalComponent
} from '../component/uicomponent/bd-handover-approval/bd-handover-approval.component';
import {
    BdPauseDialogComponent
} from '../component/uicomponent/bd-pause-dialog/bd-pause-dialog.component';
import {
    BdPlanBreakDecisionComponent
} from '../component/uicomponent/bd-plan-break-decision/bd-plan-break-decision.component';
import {
    BdHandoverConfigurationComponent
} from '../component/uicomponent/bd-handover-configuration/bd-handover-configuration.component';
import {
    BdWriteOffDialogComponent
} from '../component/uicomponent/bd-write-off-dialog/bd-write-off-dialog.component';
import {
    BdRateAgreementDialogComponent
} from '../component/uicomponent/bd-rate-agreement-dialog/bd-rate-agreement-dialog.component';
import {
    BdEscalationDialogComponent
} from '../component/uicomponent/bd-escalation-dialog/bd-escalation-dialog.component';
import {
    BdGenericFormDialogComponent
} from '../component/uicomponent/bd-generic-form-dialog/bd-generic-form-dialog.component';

/**
 * AI:
 * Status: 'generated'
 * Type: AppInitializer
 * SubType: ComponentRegistry-Bootstrap
 * Reason: Registers all Wave-3/4 UI components with the
 *         {@link BdDynamicUiComponentLoaderService} at application bootstrap so the
 *         {@link BdOpenActionDrawerComponent} can render them dynamically when the
 *         agent clicks 'Aktion ausführen'.
 *
 *         Wave 4 changes:
 *           - {@link BdHandoverConfigurationComponent} is now registered under
 *             {@code HANDOVER_CONFIGURATION} (Ordinal 13) instead of the
 *             provisional {@code INKA_CONFIRMATION} mapping from Wave 3.
 *             Both registrations are kept so existing backend data that still
 *             references {@code INKA_CONFIRMATION} continues to work.
 *           - Four new Wave-4 dialog components are registered:
 *             WRITE_OFF_DIALOG, RATE_AGREEMENT_DIALOG, ESCALATION_DIALOG,
 *             GENERIC_FORM.
 *
 *         Mapping:
 *           - CONTRACT_DECISION_TABLE  → {@link BdContractDecisionTableComponent}
 *           - LV_DECISION_TABLE        → {@link BdLvDecisionTableComponent}
 *           - HANDOVER_APPROVAL        → {@link BdHandoverApprovalComponent}
 *           - PAUSE_DIALOG             → {@link BdPauseDialogComponent}
 *           - PLAN_BREAK_DECISION      → {@link BdPlanBreakDecisionComponent}
 *           - HANDOVER_CONFIGURATION   → {@link BdHandoverConfigurationComponent}
 *               (Wave 4 — dedicated enum value for SB pre-approval config step)
 *           - INKA_CONFIRMATION        → {@link BdHandoverConfigurationComponent}
 *               (Legacy fallback — kept for backward compatibility with existing
 *                Dunning rows that still carry INKA_CONFIRMATION in uiComponent)
 *           - WRITE_OFF_DIALOG         → {@link BdWriteOffDialogComponent}
 *           - RATE_AGREEMENT_DIALOG    → {@link BdRateAgreementDialogComponent}
 *           - ESCALATION_DIALOG        → {@link BdEscalationDialogComponent}
 *           - GENERIC_FORM             → {@link BdGenericFormDialogComponent}
 *
 * TODO: Backend — add HANDOVER_CONFIGURATION (Ordinal 13) to
 *       com.mvs.backend.bd.enums.BdDunningExitUiComponent.
 *
 * @ticket TM-1000001782
 * @ticket TM-1000001786
 */
export function registerBdUiComponents(
    loader: BdDynamicUiComponentLoaderService
): () => void {
    return () => {
        loader.registerComponent(
            BdDunningExitUiComponent.CONTRACT_DECISION_TABLE,
            BdContractDecisionTableComponent
        );
        loader.registerComponent(
            BdDunningExitUiComponent.LV_DECISION_TABLE,
            BdLvDecisionTableComponent
        );
        loader.registerComponent(
            BdDunningExitUiComponent.HANDOVER_APPROVAL,
            BdHandoverApprovalComponent
        );
        loader.registerComponent(
            BdDunningExitUiComponent.PAUSE_DIALOG,
            BdPauseDialogComponent
        );
        loader.registerComponent(
            BdDunningExitUiComponent.PLAN_BREAK_DECISION,
            BdPlanBreakDecisionComponent
        );
        // Wave 4: Dedicated enum value for SB pre-approval configuration.
        loader.registerComponent(
            BdDunningExitUiComponent.HANDOVER_CONFIGURATION,
            BdHandoverConfigurationComponent
        );
        // Legacy fallback: keeps backward compatibility for Dunning rows that
        // still reference INKA_CONFIRMATION from before the Wave 4 enum migration.
        loader.registerComponent(
            BdDunningExitUiComponent.INKA_CONFIRMATION,
            BdHandoverConfigurationComponent
        );
        // Wave 4: Four new dialog components for the remaining exit-UI types.
        loader.registerComponent(
            BdDunningExitUiComponent.WRITE_OFF_DIALOG,
            BdWriteOffDialogComponent
        );
        loader.registerComponent(
            BdDunningExitUiComponent.RATE_AGREEMENT_DIALOG,
            BdRateAgreementDialogComponent
        );
        loader.registerComponent(
            BdDunningExitUiComponent.ESCALATION_DIALOG,
            BdEscalationDialogComponent
        );
        loader.registerComponent(
            BdDunningExitUiComponent.GENERIC_FORM,
            BdGenericFormDialogComponent
        );
    };
}

/**
 * APP_INITIALIZER provider that wires {@link registerBdUiComponents} into the
 * Angular bootstrap phase.
 */
export const BD_UI_COMPONENT_REGISTRY_INITIALIZER: Provider = {
    provide: APP_INITIALIZER,
    useFactory: registerBdUiComponents,
    deps: [BdDynamicUiComponentLoaderService],
    multi: true
};
